import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db, auth } from '../services/firebase';
import { doc, getDoc, setDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { useClub } from '../contexts/ClubContext';
import Navbar from '../components/Navbar';

function PlayerDashboard() {
  const [user, authLoading] = useAuthState(auth);
  const { club } = useClub();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [playerData, setPlayerData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [registrationStatus, setRegistrationStatus] = useState(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Load user data and player info
  useEffect(() => {
    const loadPlayerData = async () => {
      if (!user || !club) return;
      
      setLoading(true);
      try {
        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        let foundPlayer = null;
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          
          // Check if user has a linked player profile
          if (data.playerId) {
            const playerDoc = await getDoc(doc(db, 'players', data.playerId));
            if (playerDoc.exists()) {
              foundPlayer = { id: playerDoc.id, ...playerDoc.data() };
            }
          }
        }
        
        // If no linked player, try to find by email
        if (!foundPlayer && user.email) {
          const playersQuery = query(
            collection(db, 'players'),
            where('clubId', '==', club.id),
            where('email', '==', user.email)
          );
          const playersSnapshot = await getDocs(playersQuery);
          if (!playersSnapshot.empty) {
            foundPlayer = { id: playersSnapshot.docs[0].id, ...playersSnapshot.docs[0].data() };
            
            // Link the player to the user account
            if (userDoc.exists()) {
              await setDoc(doc(db, 'users', user.uid), {
                ...userDoc.data(),
                playerId: foundPlayer.id
              }, { merge: true });
            }
          }
        }
        
        if (foundPlayer) {
          setPlayerData(foundPlayer);
          
          // Get team info if player has a team
          if (foundPlayer.team) {
            const teamsQuery = query(
              collection(db, 'teams'),
              where('clubId', '==', club.id),
              where('name', '==', foundPlayer.team)
            );
            const teamsSnapshot = await getDocs(teamsQuery);
            if (!teamsSnapshot.empty) {
              setTeamData({ id: teamsSnapshot.docs[0].id, ...teamsSnapshot.docs[0].data() });
            }
          }
        }

        // Check for pending registration
        const regQuery = query(
          collection(db, 'registrations'),
          where('clubId', '==', club.id),
          where('email', '==', user.email)
        );
        const regSnapshot = await getDocs(regQuery);
        if (!regSnapshot.empty) {
          const reg = regSnapshot.docs[0].data();
          setRegistrationStatus(reg.status);
        }

        // Load upcoming events for this club
        const now = new Date();
        const eventsQuery = query(
          collection(db, 'events'),
          where('clubId', '==', club.id),
          where('date', '>=', now.toISOString().split('T')[0]),
          orderBy('date', 'asc'),
          limit(5)
        );
        const eventsSnapshot = await getDocs(eventsQuery);
        const events = eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUpcomingEvents(events);

        // Load recent attendance if player exists
        if (foundPlayer?.id) {
          const attendanceQuery = query(
            collection(db, 'attendance'),
            where('clubId', '==', club.id),
            where('playerId', '==', foundPlayer.id),
            orderBy('date', 'desc'),
            limit(10)
          );
          const attendanceSnapshot = await getDocs(attendanceQuery);
          const attendance = attendanceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setRecentAttendance(attendance);
        }

      } catch (error) {
        console.error('Error loading player data:', error);
      }
      setLoading(false);
    };

    loadPlayerData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, club]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      approved: 'status-approved',
      rejected: 'status-rejected'
    };
    return <span className={`status-badge ${statusClasses[status] || ''}`}>{status}</span>;
  };

  const getAttendanceBadge = (status) => {
    const badges = {
      present: { class: 'attendance-present', label: 'Present' },
      absent: { class: 'attendance-absent', label: 'Absent' },
      excused: { class: 'attendance-excused', label: 'Excused' }
    };
    const badge = badges[status] || badges.absent;
    return <span className={`attendance-badge ${badge.class}`}>{badge.label}</span>;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-TT', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (authLoading || loading) {
    return (
      <div className="player-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="player-dashboard-page">
      <Navbar />
      
      <div className="player-dashboard-container">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1>My Dashboard</h1>
            <p>Welcome back, {userData?.name || user?.email?.split('@')[0]}!</p>
          </div>
          <button onClick={handleLogout} className="btn-logout">
            Logout
          </button>
        </div>

        {/* Profile Card */}
        <div className="dashboard-grid">
          <div className="dashboard-card profile-card">
            <div className="card-header">
              <h2>My Profile</h2>
            </div>
            <div className="card-body">
              {playerData ? (
                <div className="player-profile">
                  <div className="profile-image">
                    {playerData.image ? (
                      <img src={playerData.image} alt={playerData.name} />
                    ) : (
                      <div className="profile-placeholder">
                        {playerData.name?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>
                  <div className="profile-info">
                    <h3>{playerData.name}</h3>
                    <p className="player-position">{playerData.position}</p>
                    <div className="profile-details">
                      <div className="detail-item">
                        <span className="detail-label">Number</span>
                        <span className="detail-value">#{playerData.number}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Team</span>
                        <span className="detail-value">{playerData.team || 'Unassigned'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Region</span>
                        <span className="detail-value">{playerData.region || 'TT'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="player-stats-grid">
                    <div className="stat-box">
                      <span className="stat-value">{playerData.goals || 0}</span>
                      <span className="stat-label">Goals</span>
                    </div>
                    <div className="stat-box">
                      <span className="stat-value">{playerData.assists || 0}</span>
                      <span className="stat-label">Assists</span>
                    </div>
                    <div className="stat-box">
                      <span className="stat-value">{playerData.rating || '-'}</span>
                      <span className="stat-label">Rating</span>
                    </div>
                  </div>
                </div>
              ) : registrationStatus ? (
                <div className="registration-status">
                  <div className="status-icon">
                    {registrationStatus === 'pending' && '⏳'}
                    {registrationStatus === 'approved' && '✓'}
                    {registrationStatus === 'rejected' && '✗'}
                  </div>
                  <h3>Registration Status</h3>
                  {getStatusBadge(registrationStatus)}
                  <p>
                    {registrationStatus === 'pending' && 'Your registration is being reviewed by the club administrator.'}
                    {registrationStatus === 'approved' && 'Your registration has been approved! Your player profile will be created soon.'}
                    {registrationStatus === 'rejected' && 'Your registration was not approved. Please contact the club for more information.'}
                  </p>
                </div>
              ) : (
                <div className="no-profile">
                  <div className="no-profile-icon">👤</div>
                  <h3>No Player Profile Yet</h3>
                  <p>Register with the club to get your player profile.</p>
                  <Link to="/register" className="btn-primary">
                    Register Now
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Team Info Card */}
          <div className="dashboard-card team-card">
            <div className="card-header">
              <h2>My Team</h2>
              {teamData && <Link to="/teams" className="view-link">View All Teams</Link>}
            </div>
            <div className="card-body">
              {teamData ? (
                <div className="team-info">
                  <div className="team-header">
                    <div className="team-badge" style={{ background: teamData.color || '#1e3a5f' }}>
                      {teamData.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <h3>{teamData.name}</h3>
                      <p className="team-age-group">{teamData.ageGroup || 'Senior'}</p>
                    </div>
                  </div>
                  <div className="team-details">
                    <div className="detail-row">
                      <span className="detail-icon">👨‍🏫</span>
                      <span>Coach: {teamData.coach || 'TBD'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-icon">📍</span>
                      <span>Training: {teamData.trainingSchedule || 'Check schedule'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-icon">👥</span>
                      <span>{teamData.playerCount || 0} Players</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-team">
                  <div className="no-team-icon">⚽</div>
                  <p>Not assigned to a team yet</p>
                  <Link to="/teams" className="btn-secondary">
                    Browse Teams
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Events Card */}
          <div className="dashboard-card events-card">
            <div className="card-header">
              <h2>Upcoming Events</h2>
              <Link to="/schedule" className="view-link">View Full Schedule</Link>
            </div>
            <div className="card-body">
              {upcomingEvents.length > 0 ? (
                <div className="events-list">
                  {upcomingEvents.map(event => (
                    <div key={event.id} className="event-item">
                      <div className="event-date-box">
                        <span className="event-day">{new Date(event.date).getDate()}</span>
                        <span className="event-month">{new Date(event.date).toLocaleString('default', { month: 'short' })}</span>
                      </div>
                      <div className="event-info">
                        <h4>{event.title}</h4>
                        <p className="event-meta">
                          <span className="event-time">{formatTime(event.time)}</span>
                          {event.location && <span className="event-location">@ {event.location}</span>}
                        </p>
                        {event.team && <span className="event-team-badge">{event.team}</span>}
                      </div>
                      <div className={`event-type-indicator ${event.type || 'training'}`}></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-events">
                  <div className="no-events-icon">📅</div>
                  <p>No upcoming events scheduled</p>
                </div>
              )}
            </div>
          </div>

          {/* Attendance Card */}
          <div className="dashboard-card attendance-card">
            <div className="card-header">
              <h2>My Attendance</h2>
            </div>
            <div className="card-body">
              {playerData && recentAttendance.length > 0 ? (
                <>
                  <div className="attendance-summary">
                    <div className="attendance-stat">
                      <span className="stat-value">
                        {recentAttendance.filter(a => a.status === 'present').length}
                      </span>
                      <span className="stat-label">Present</span>
                    </div>
                    <div className="attendance-stat">
                      <span className="stat-value">
                        {recentAttendance.filter(a => a.status === 'absent').length}
                      </span>
                      <span className="stat-label">Absent</span>
                    </div>
                    <div className="attendance-stat">
                      <span className="stat-value">
                        {recentAttendance.filter(a => a.status === 'excused').length}
                      </span>
                      <span className="stat-label">Excused</span>
                    </div>
                  </div>
                  <div className="attendance-list">
                    {recentAttendance.slice(0, 5).map(record => (
                      <div key={record.id} className="attendance-item">
                        <span className="attendance-date">{formatDate(record.date)}</span>
                        <span className="attendance-event">{record.eventTitle || 'Training'}</span>
                        {getAttendanceBadge(record.status)}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="no-attendance">
                  <div className="no-attendance-icon">📋</div>
                  <p>No attendance records yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="dashboard-card actions-card">
            <div className="card-header">
              <h2>Quick Actions</h2>
            </div>
            <div className="card-body">
              <div className="quick-actions-grid">
                <Link to="/schedule" className="quick-action">
                  <span className="action-icon">📅</span>
                  <span>View Schedule</span>
                </Link>
                <Link to="/kitshop" className="quick-action">
                  <span className="action-icon">🛒</span>
                  <span>Kit Shop</span>
                </Link>
                <Link to="/gallery" className="quick-action">
                  <span className="action-icon">📸</span>
                  <span>Photo Gallery</span>
                </Link>
                <Link to="/contact" className="quick-action">
                  <span className="action-icon">📞</span>
                  <span>Contact Club</span>
                </Link>
                {!playerData && !registrationStatus && (
                  <Link to="/register" className="quick-action highlight">
                    <span className="action-icon">✍️</span>
                    <span>Register</span>
                  </Link>
                )}
                <Link to="/teams" className="quick-action">
                  <span className="action-icon">👥</span>
                  <span>View Teams</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Club Info Card */}
          <div className="dashboard-card club-info-card">
            <div className="card-header">
              <h2>Club Information</h2>
            </div>
            <div className="card-body">
              <div className="club-details">
                <h3>{club?.name}</h3>
                <div className="club-info-row">
                  <span className="info-icon">📍</span>
                  <span>{club?.location || club?.address || 'Trinidad & Tobago'}</span>
                </div>
                {club?.email && (
                  <div className="club-info-row">
                    <span className="info-icon">📧</span>
                    <a href={`mailto:${club.email}`}>{club.email}</a>
                  </div>
                )}
                {club?.phone && (
                  <div className="club-info-row">
                    <span className="info-icon">📞</span>
                    <a href={`tel:${club.phone}`}>{club.phone}</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlayerDashboard;
